import { Allergen } from "../../../admin/products/productsTypes"

import { defaultCellValues, isFieldLocked } from "./weekMenuLib"
import { WeekDishRow, WeekMenuCellValues } from "./weekMenuTypes"

// Pre-scripted dishes standing in for the AI backend, with the Danish test allergen names.

export enum AiMenuTheme {
    SURPRISE = "SURPRISE",
    ASIAN = "ASIAN",
    MEDITERRANEAN = "MEDITERRANEAN",
    NORDIC = "NORDIC",
    ITALIAN = "ITALIAN",
    MEXICAN = "MEXICAN",
    INDIAN = "INDIAN",
    MIDDLE_EASTERN = "MIDDLE_EASTERN",
}

export enum AiSpiceLevel {
    LOW = "LOW",
    MEDIUM = "MEDIUM",
    SPICY = "SPICY",
}

export enum AiHealthiness {
    HEALTHY = "HEALTHY",
    BALANCED = "BALANCED",
    COMFORT = "COMFORT",
}

export enum AiMenuStyle {
    NORMAL = "NORMAL",
    GOURMET = "GOURMET",
    FUN = "FUN",
    PARTY = "PARTY",
    CELEBRATION = "CELEBRATION",
}

export type AiDaySettings = Readonly<{
    theme: AiMenuTheme
    spice: AiSpiceLevel
    healthiness: AiHealthiness
    style: AiMenuStyle
    ingredients: ReadonlyArray<string>
    inspiration: string
}>

export function defaultAiSettings(): AiDaySettings {
    return {
        theme: AiMenuTheme.SURPRISE,
        spice: AiSpiceLevel.MEDIUM,
        healthiness: AiHealthiness.BALANCED,
        style: AiMenuStyle.NORMAL,
        ingredients: [],
        inspiration: "",
    }
}

// TODO: fetch the suggested ingredients from the API once it exists; English-only placeholders.
export const AI_SUGGESTED_INGREDIENTS: ReadonlyArray<string> = [
    "Chicken",
    "Beef",
    "Salmon",
    "Tofu",
    "Potatoes",
    "Tomatoes",
    "Rice",
    "Chickpeas",
    "Mushrooms",
    "Spinach",
]

type BankDish = Readonly<{
    title: string
    description: string
    allergenNames: ReadonlyArray<string>
    price: string
    marketingTag?: string
}>

type BankEntry = Readonly<{ hot: BankDish; veg: BankDish }>

const BANK: Readonly<Record<AiMenuTheme, ReadonlyArray<BankEntry>>> = {
    [AiMenuTheme.SURPRISE]: [
        {
            hot: {
                title: "Roast chicken with thyme jus",
                description: "Free-range chicken, roasted root vegetables and a thyme pan jus.",
                allergenNames: [],
                price: "72",
            },
            veg: {
                title: "Wild mushroom risotto",
                description: "Creamy arborio rice with wild mushrooms and parmesan.",
                allergenNames: ["Mælk"],
                price: "64",
            },
        },
        {
            hot: {
                title: "Braised beef with mash",
                description: "Slow-braised beef in red wine with buttery potato mash.",
                allergenNames: ["Mælk"],
                price: "78",
            },
            veg: {
                title: "Falafel & quinoa bowl",
                description: "Crispy falafel, quinoa, roasted veg and tahini dressing.",
                allergenNames: ["Sesam"],
                price: "60",
            },
        },
        {
            hot: {
                title: "Pan-fried cod, lemon butter",
                description: "Cod fillet with lemon-butter sauce and new potatoes.",
                allergenNames: ["Fisk", "Mælk"],
                price: "80",
            },
            veg: {
                title: "Spinach & feta filo pie",
                description: "Filo pie with spinach, feta and fresh herbs.",
                allergenNames: ["Gluten", "Mælk", "Æg"],
                price: "62",
            },
        },
        {
            hot: {
                title: "Pork schnitzel",
                description: "Crispy breaded pork with a creamy mushroom sauce.",
                allergenNames: ["Gluten", "Mælk", "Æg"],
                price: "74",
            },
            veg: {
                title: "Sweet potato & bean stew",
                description: "Hearty sweet potato, black bean and corn stew.",
                allergenNames: [],
                price: "58",
            },
        },
        {
            hot: {
                title: "Herb-roasted salmon",
                description: "Salmon with a herb crust, served with seasonal greens.",
                allergenNames: ["Fisk"],
                price: "82",
                marketingTag: "Chef's choice",
            },
            veg: {
                title: "Halloumi & grain salad",
                description: "Grilled halloumi over a warm grain and roasted veg salad.",
                allergenNames: ["Mælk"],
                price: "63",
            },
        },
    ],
    [AiMenuTheme.ASIAN]: [
        {
            hot: {
                title: "Teriyaki chicken bowl",
                description: "Glazed teriyaki chicken with steamed rice and edamame.",
                allergenNames: ["Soja", "Gluten"],
                price: "70",
            },
            veg: {
                title: "Tofu pad thai",
                description: "Rice noodles, tofu, peanuts and tamarind sauce.",
                allergenNames: ["Jordnødder", "Soja"],
                price: "62",
            },
        },
        {
            hot: {
                title: "Korean bulgogi beef",
                description: "Marinated beef with sesame, served on jasmine rice.",
                allergenNames: ["Soja", "Sesam"],
                price: "76",
                marketingTag: "Spicy",
            },
            veg: {
                title: "Miso aubergine donburi",
                description: "Glazed miso aubergine over rice with pickles.",
                allergenNames: ["Soja"],
                price: "60",
            },
        },
        {
            hot: {
                title: "Thai green chicken curry",
                description: "Chicken in green curry with bamboo and Thai basil.",
                allergenNames: ["Fisk"],
                price: "72",
                marketingTag: "Spicy",
            },
            veg: {
                title: "Vegetable bibimbap",
                description: "Rice bowl with seasoned vegetables and gochujang.",
                allergenNames: ["Soja", "Sesam"],
                price: "59",
            },
        },
        {
            hot: {
                title: "Japanese katsu curry",
                description: "Crispy chicken katsu with a mild curry sauce and rice.",
                allergenNames: ["Gluten"],
                price: "71",
            },
            veg: {
                title: "Mapo tofu",
                description: "Silken tofu in a savoury Sichuan-style sauce.",
                allergenNames: ["Soja"],
                price: "58",
                marketingTag: "Spicy",
            },
        },
        {
            hot: {
                title: "Sweet & sour pork",
                description: "Crispy pork with peppers and pineapple, jasmine rice.",
                allergenNames: ["Gluten", "Soja"],
                price: "70",
            },
            veg: {
                title: "Singapore noodles",
                description: "Curried rice vermicelli with vegetables and egg.",
                allergenNames: ["Æg", "Soja"],
                price: "60",
            },
        },
    ],
    [AiMenuTheme.MEDITERRANEAN]: [
        {
            hot: {
                title: "Greek lemon chicken",
                description: "Oregano-lemon chicken with potatoes and olives.",
                allergenNames: [],
                price: "71",
            },
            veg: {
                title: "Stuffed bell peppers",
                description: "Peppers filled with rice, herbs and pine nuts.",
                allergenNames: ["Nødder"],
                price: "60",
            },
        },
        {
            hot: {
                title: "Grilled sea bream",
                description: "Whole sea bream with herbs, lemon and capers.",
                allergenNames: ["Fisk"],
                price: "82",
                marketingTag: "Chef's choice",
            },
            veg: {
                title: "Vegetable moussaka",
                description: "Layered aubergine, lentils and bechamel.",
                allergenNames: ["Mælk", "Gluten"],
                price: "62",
            },
        },
        {
            hot: {
                title: "Lamb souvlaki",
                description: "Marinated lamb skewers with tzatziki and pita.",
                allergenNames: ["Mælk", "Gluten"],
                price: "78",
            },
            veg: {
                title: "Falafel mezze plate",
                description: "Falafel, hummus, tabbouleh and flatbread.",
                allergenNames: ["Sesam", "Gluten"],
                price: "59",
            },
        },
        {
            hot: {
                title: "Chicken shawarma",
                description: "Spiced chicken with garlic sauce and salad.",
                allergenNames: ["Mælk"],
                price: "70",
            },
            veg: {
                title: "Spanakopita",
                description: "Spinach and feta in crisp filo pastry.",
                allergenNames: ["Gluten", "Mælk", "Æg"],
                price: "58",
            },
        },
        {
            hot: {
                title: "Seafood paella",
                description: "Saffron rice with mussels, prawns and squid.",
                allergenNames: ["Skaldyr", "Fisk"],
                price: "84",
            },
            veg: {
                title: "Caponata with polenta",
                description: "Sicilian sweet-sour aubergine over soft polenta.",
                allergenNames: [],
                price: "57",
            },
        },
    ],
    [AiMenuTheme.NORDIC]: [
        {
            hot: {
                title: "Pan-fried plaice, remoulade",
                description: "Crisp plaice with remoulade and new potatoes.",
                allergenNames: ["Fisk", "Gluten", "Æg"],
                price: "79",
            },
            veg: {
                title: "Root vegetable hash",
                description: "Caramelised roots with a poached egg and rye.",
                allergenNames: ["Æg", "Gluten"],
                price: "58",
            },
        },
        {
            hot: {
                title: "Frikadeller with red cabbage",
                description: "Danish pork meatballs with braised red cabbage.",
                allergenNames: ["Gluten", "Mælk"],
                price: "72",
            },
            veg: {
                title: "Beetroot & barley bowl",
                description: "Roasted beetroot, pearl barley and dill yoghurt.",
                allergenNames: ["Gluten", "Mælk"],
                price: "57",
            },
        },
        {
            hot: {
                title: "Baked salmon, dill",
                description: "Salmon with dill, cucumber salad and potatoes.",
                allergenNames: ["Fisk"],
                price: "81",
            },
            veg: {
                title: "Mushroom rye toast",
                description: "Creamed wild mushrooms on toasted rye.",
                allergenNames: ["Gluten", "Mælk"],
                price: "55",
            },
        },
        {
            hot: {
                title: "Beef hash (biksemad)",
                description: "Classic Danish potato and beef hash with fried egg.",
                allergenNames: ["Æg"],
                price: "70",
            },
            veg: {
                title: "Kale & potato cakes",
                description: "Crispy kale-potato cakes with herb cream.",
                allergenNames: ["Mælk", "Gluten"],
                price: "56",
            },
        },
        {
            hot: {
                title: "Smoked haddock chowder",
                description: "Creamy smoked haddock and leek chowder.",
                allergenNames: ["Fisk", "Mælk"],
                price: "68",
            },
            veg: {
                title: "Jerusalem artichoke soup",
                description: "Velvety artichoke soup with toasted hazelnuts.",
                allergenNames: ["Nødder", "Mælk"],
                price: "52",
            },
        },
    ],
    [AiMenuTheme.ITALIAN]: [
        {
            hot: {
                title: "Chicken parmigiana",
                description: "Breaded chicken, tomato sauce and mozzarella.",
                allergenNames: ["Gluten", "Mælk"],
                price: "73",
            },
            veg: {
                title: "Wild mushroom risotto",
                description: "Creamy arborio rice with wild mushrooms and parmesan.",
                allergenNames: ["Mælk"],
                price: "63",
            },
        },
        {
            hot: {
                title: "Beef ragu pappardelle",
                description: "Slow-cooked beef ragu with wide pasta ribbons.",
                allergenNames: ["Gluten"],
                price: "75",
                marketingTag: "Chef's choice",
            },
            veg: {
                title: "Eggplant parmigiana",
                description: "Baked aubergine with tomato and mozzarella.",
                allergenNames: ["Mælk"],
                price: "61",
            },
        },
        {
            hot: {
                title: "Saltimbocca alla romana",
                description: "Veal with prosciutto and sage in white wine.",
                allergenNames: [],
                price: "80",
            },
            veg: {
                title: "Gnocchi al pomodoro",
                description: "Soft potato gnocchi in a basil tomato sauce.",
                allergenNames: ["Gluten", "Mælk"],
                price: "59",
            },
        },
        {
            hot: {
                title: "Calabrese sausage pasta",
                description: "Spicy sausage and fennel rigatoni.",
                allergenNames: ["Gluten"],
                price: "70",
                marketingTag: "Spicy",
            },
            veg: {
                title: "Caprese & focaccia",
                description: "Buffalo mozzarella, tomato, basil and focaccia.",
                allergenNames: ["Mælk", "Gluten"],
                price: "58",
            },
        },
        {
            hot: {
                title: "Pan-seared branzino",
                description: "Sea bass with lemon, capers and rocket.",
                allergenNames: ["Fisk"],
                price: "82",
            },
            veg: {
                title: "Pumpkin tortelli",
                description: "Pumpkin-filled pasta in sage butter.",
                allergenNames: ["Gluten", "Mælk", "Æg"],
                price: "62",
            },
        },
    ],
    [AiMenuTheme.MEXICAN]: [
        {
            hot: {
                title: "Chicken tinga tacos",
                description: "Smoky chipotle chicken with lime and slaw.",
                allergenNames: ["Gluten"],
                price: "68",
                marketingTag: "Spicy",
            },
            veg: {
                title: "Black bean enchiladas",
                description: "Tortillas filled with beans and cheese in red sauce.",
                allergenNames: ["Gluten", "Mælk"],
                price: "59",
            },
        },
        {
            hot: {
                title: "Carne asada bowl",
                description: "Grilled steak, rice, beans, salsa and guacamole.",
                allergenNames: [],
                price: "76",
            },
            veg: {
                title: "Sweet potato tacos",
                description: "Roasted sweet potato, black beans and chipotle crema.",
                allergenNames: ["Mælk", "Gluten"],
                price: "58",
            },
        },
        {
            hot: {
                title: "Pork carnitas",
                description: "Slow-cooked pulled pork with pickled onion.",
                allergenNames: [],
                price: "72",
            },
            veg: {
                title: "Chilli sin carne",
                description: "Smoky bean and vegetable chilli with rice.",
                allergenNames: [],
                price: "56",
                marketingTag: "Spicy",
            },
        },
        {
            hot: {
                title: "Baja fish tacos",
                description: "Crispy fish, cabbage slaw and lime crema.",
                allergenNames: ["Fisk", "Gluten", "Mælk"],
                price: "70",
            },
            veg: {
                title: "Elote & quinoa bowl",
                description: "Charred corn, quinoa, beans and cotija.",
                allergenNames: ["Mælk"],
                price: "57",
            },
        },
        {
            hot: {
                title: "Chicken fajitas",
                description: "Sizzling chicken with peppers and warm tortillas.",
                allergenNames: ["Gluten"],
                price: "71",
            },
            veg: {
                title: "Mushroom quesadilla",
                description: "Griddled tortilla with mushrooms and melted cheese.",
                allergenNames: ["Gluten", "Mælk"],
                price: "55",
            },
        },
    ],
    [AiMenuTheme.INDIAN]: [
        {
            hot: {
                title: "Butter chicken",
                description: "Tandoori chicken in a creamy tomato sauce, basmati rice.",
                allergenNames: ["Mælk"],
                price: "72",
            },
            veg: {
                title: "Chana masala",
                description: "Spiced chickpea curry with rice and coriander.",
                allergenNames: [],
                price: "58",
            },
        },
        {
            hot: {
                title: "Lamb rogan josh",
                description: "Slow-cooked lamb in aromatic Kashmiri spices.",
                allergenNames: [],
                price: "78",
                marketingTag: "Spicy",
            },
            veg: {
                title: "Paneer tikka masala",
                description: "Grilled paneer in a rich spiced gravy.",
                allergenNames: ["Mælk"],
                price: "62",
            },
        },
        {
            hot: {
                title: "Chicken biryani",
                description: "Fragrant layered rice with spiced chicken.",
                allergenNames: ["Mælk"],
                price: "70",
            },
            veg: {
                title: "Aloo gobi",
                description: "Potato and cauliflower with turmeric and cumin.",
                allergenNames: [],
                price: "55",
            },
        },
        {
            hot: {
                title: "Goan fish curry",
                description: "Coconut and tamarind fish curry with rice.",
                allergenNames: ["Fisk"],
                price: "74",
                marketingTag: "Spicy",
            },
            veg: {
                title: "Dal tadka",
                description: "Yellow lentils tempered with garlic and cumin.",
                allergenNames: [],
                price: "52",
            },
        },
        {
            hot: {
                title: "Chicken korma",
                description: "Mild creamy almond chicken curry with naan.",
                allergenNames: ["Mælk", "Nødder", "Gluten"],
                price: "71",
            },
            veg: {
                title: "Saag paneer",
                description: "Spinach and paneer in a lightly spiced sauce.",
                allergenNames: ["Mælk"],
                price: "60",
            },
        },
    ],
    [AiMenuTheme.MIDDLE_EASTERN]: [
        {
            hot: {
                title: "Chicken shish taouk",
                description: "Garlic-marinated chicken skewers with rice and sumac.",
                allergenNames: ["Mælk"],
                price: "71",
            },
            veg: {
                title: "Falafel & hummus plate",
                description: "Falafel, hummus, salad and warm flatbread.",
                allergenNames: ["Sesam", "Gluten"],
                price: "58",
            },
        },
        {
            hot: {
                title: "Lamb kofta",
                description: "Spiced lamb kofta with tahini and flatbread.",
                allergenNames: ["Sesam", "Gluten"],
                price: "76",
            },
            veg: {
                title: "Mujadara",
                description: "Lentils and rice with crispy onions and yoghurt.",
                allergenNames: ["Mælk"],
                price: "55",
            },
        },
        {
            hot: {
                title: "Chicken maqluba",
                description: "Upside-down spiced rice with chicken and aubergine.",
                allergenNames: [],
                price: "72",
            },
            veg: {
                title: "Stuffed vine leaves",
                description: "Rice-stuffed vine leaves with lemon and herbs.",
                allergenNames: [],
                price: "56",
            },
        },
        {
            hot: {
                title: "Baked kibbeh",
                description: "Bulgur and beef bake with pine nuts.",
                allergenNames: ["Gluten", "Nødder"],
                price: "70",
            },
            veg: {
                title: "Shakshuka",
                description: "Eggs poached in a spiced tomato and pepper sauce.",
                allergenNames: ["Æg"],
                price: "54",
            },
        },
        {
            hot: {
                title: "Grilled halloumi fattoush",
                description: "Crisp salad with sumac dressing and halloumi.",
                allergenNames: ["Mælk", "Gluten"],
                price: "60",
            },
            veg: {
                title: "Batata harra",
                description: "Spiced fried potatoes with coriander and chilli.",
                allergenNames: [],
                price: "52",
                marketingTag: "Spicy",
            },
        },
    ],
}

function isVegRow(row: WeekDishRow): boolean {
    return /veg|salad|green|grain|smoothie/i.test(row.name)
}

function matchAllergens(names: ReadonlyArray<string>, options: ReadonlyArray<Allergen>): Allergen[] {
    return options.filter((allergen) => names.some((name) => name.toLowerCase() === allergen.name.toLowerCase()))
}

/** Picks a themed dish for a row and day, then applies the settings modifiers. */
export function pickBankDish(
    row: WeekDishRow,
    dayIndex: number,
    settings: AiDaySettings,
    allergenOptions: ReadonlyArray<Allergen>,
    entryIndex?: number,
): WeekMenuCellValues {
    const bank = BANK[settings.theme]
    const entry = bank[(((entryIndex ?? dayIndex) % bank.length) + bank.length) % bank.length]
    const dish = isVegRow(row) ? entry.veg : entry.hot

    let description = dish.description
    if (settings.ingredients.length > 0) {
        description = `${description.replace(/\.$/, "")}. With ${settings.ingredients.join(", ").toLowerCase()}.`
    }
    let marketingTag = dish.marketingTag ?? ""
    if (settings.spice === AiSpiceLevel.SPICY && row.marketingTag.isShown) marketingTag = "Spicy"

    const defaults = defaultCellValues(row)
    return {
        title: dish.title,
        description: row.description.isShown && !isFieldLocked(row.description) ? description : defaults.description,
        allergens: row.allergens.isShown ? matchAllergens(dish.allergenNames, allergenOptions) : [],
        price: row.price.isShown && !isFieldLocked(row.price) ? dish.price : defaults.price,
        marketingTag: row.marketingTag.isShown ? marketingTag : "",
        photo: defaults.photo,
        details: defaults.details,
    }
}

export function bankSizeFor(theme: AiMenuTheme): number {
    return BANK[theme].length
}
